import {rtkQueryApi} from '~/shared/api/rtk-query-api';
import {buildFiltersParams} from '~/shared/utils/buildFiltersParams';

export interface CtaLanguageDefinition {
  id?: number;
  code: string;
  label: string;
  name?: string;
  native_label?: string;
  sort?: number;
  is_default: boolean;
  is_active?: boolean;
}

export interface CtaDictionaryItemDto {
  id: number;
  key: string;
  sort: number;
  is_active: boolean;
  texts: Record<string, string>;
  translations: Array<{
    language_code: string;
    text: string;
  }>;
  updated_at?: string;
}

export interface CtaDictionaryListResponse {
  data: CtaDictionaryItemDto[];
  pagination: {
    total: number;
    page: number;
    page_size: number;
    last_page?: number;
  };
  meta: {
    languages: CtaLanguageDefinition[];
  };
}

export interface CtaDictionaryOptionsResponse {
  data: {
    default_language_code?: string;
    languages: CtaLanguageDefinition[];
    items: Array<{
      id?: number;
      key: string;
      sort?: number;
      is_active?: boolean;
      texts: Record<string, string>;
    }>;
  };
}

export interface SaveCtaLanguagePayload {
  code: string;
  name: string;
  native_label: string;
  sort: number;
  is_default: boolean;
  is_active: boolean;
}

export interface SaveCtaDictionaryPayload {
  key: string;
  sort: number;
  is_active: boolean;
  translations: Array<{
    language_code: string;
    text: string;
  }>;
}

export const ctaDictionaryApi = rtkQueryApi
  .enhanceEndpoints({
    addTagTypes: ['cta-dictionary', 'cta-dictionary-options'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getCtaDictionaryOptions: builder.query<CtaDictionaryOptionsResponse, void>({
        query: () => ({
          url: 'dictionaries/cta/options',
          method: 'GET',
        }),
        providesTags: ['cta-dictionary-options'],
      }),
      getCtaDictionaryAdminOptions: builder.query<CtaDictionaryOptionsResponse, void>({
        query: () => ({
          url: 'dictionaries/cta/admin-options',
          method: 'GET',
        }),
        providesTags: ['cta-dictionary-options'],
      }),
      getCtaDictionaryList: builder.query<CtaDictionaryListResponse, any>({
        query: ({page, pageSize, search, filters, sort_by, sort_dir}: any) => {
          const params = new URLSearchParams();
          params.set('page', String(page));
          params.set('page_size', String(pageSize));
          if (search?.trim()) {
            params.set('search', search.trim());
          }
          if (sort_by) {
            params.set('sort_by', sort_by);
          }
          if (sort_dir) {
            params.set('sort_dir', sort_dir);
          }

          const filtersParams = buildFiltersParams(filters || {});

          return {
            url: `dictionaries/cta?${params.toString()}&${filtersParams}`,
            method: 'GET',
          };
        },
        providesTags: ['cta-dictionary'],
      }),
      createCtaDictionaryItem: builder.mutation<any, SaveCtaDictionaryPayload>({
        query: payload => ({
          url: 'dictionaries/cta',
          method: 'POST',
          body: payload,
        }),
        invalidatesTags: ['cta-dictionary', 'cta-dictionary-options'],
      }),
      updateCtaDictionaryItem: builder.mutation<any, SaveCtaDictionaryPayload & {id: number}>({
        query: ({id, ...payload}) => ({
          url: `dictionaries/cta/${id}`,
          method: 'PUT',
          body: payload,
        }),
        invalidatesTags: ['cta-dictionary', 'cta-dictionary-options'],
      }),
      createCtaLanguage: builder.mutation<any, SaveCtaLanguagePayload>({
        query: payload => ({
          url: 'dictionaries/cta/languages',
          method: 'POST',
          body: payload,
        }),
        invalidatesTags: ['cta-dictionary', 'cta-dictionary-options'],
      }),
      updateCtaLanguage: builder.mutation<any, SaveCtaLanguagePayload & {id: number}>({
        query: ({id, ...payload}) => ({
          url: `dictionaries/cta/languages/${id}`,
          method: 'PUT',
          body: payload,
        }),
        invalidatesTags: ['cta-dictionary', 'cta-dictionary-options'],
      }),
      updateCtaTranslation: builder.mutation<any, { itemId: number; languageCode: string; text: string }>({
        query: ({itemId, languageCode, text}) => ({
          url: `dictionaries/cta/translations/${itemId}/${encodeURIComponent(languageCode)}`,
          method: 'PUT',
          body: {text},
        }),
        invalidatesTags: ['cta-dictionary', 'cta-dictionary-options'],
      }),
    }),
  });

export const {
  useGetCtaDictionaryOptionsQuery,
  useGetCtaDictionaryAdminOptionsQuery,
  useGetCtaDictionaryListQuery,
  useCreateCtaDictionaryItemMutation,
  useUpdateCtaDictionaryItemMutation,
  useCreateCtaLanguageMutation,
  useUpdateCtaLanguageMutation,
  useUpdateCtaTranslationMutation,
} = ctaDictionaryApi;
